import {
  doc,
  DocumentData,
  DocumentSnapshot,
  FirestoreDataConverter,
  getFirestore,
  SnapshotOptions,
  Timestamp,
} from 'firebase/firestore';
import {
  getWhitespaceModeFromLegacyFlag,
  isConsoleTranscriptEntryKind,
  type ConsoleTranscript,
  type ConsoleTranscriptEntry,
  type WhitespaceMode,
} from '@cip/code-runner';
import {AssignmentId} from 'assignments/types';
import {CourseId} from 'types/common';
import {
  isNullOrUndefined,
  validateOptionalPrimitiveField,
} from 'utils/general';

export interface ConsoleUnitTest {
  /** The display name for the unit test. */
  name: string;
  /** A longer human readable description. */
  description: string;
  /** A unique (per-assignment) key to the unit test. Originally stored as
   * a number, but we now store it as a string.
   */
  key: string | number;
  /** The expected sequence of inputs and outputs for the autograder. */
  transcript: ConsoleTranscript;
  /** Optional integer passed to Python's `random.seed()` before the student
   * code runs, for deterministic transcripts. */
  randomSeed?: number;
  /**
   * The assignment milestone this case belongs to, matching an `id` in the
   * assignment metadata's `milestones` list. Omitted, the case belongs to the
   * assignment as a whole and runs on every check.
   */
  milestoneId?: string;
}

export interface ConsoleUnitTestsDocument {
  unitTests: ConsoleUnitTest[];
  options?: {
    /**
     * Whether to ignore case when comparing student code to the solution code.
     * Defaults to false.
     */
    shouldIgnoreCasingInAutograder?: boolean;
    /**
     * Legacy whitespace flag — superseded by `whitespaceMode`. Kept for
     * backwards compatibility with existing assignments. Use
     * {@link getWhitespaceModeForConsoleUnitTests} to resolve the effective
     * mode rather than reading this field directly.
     */
    shouldIgnoreWhitespaceInAutograder?: boolean;
    /**
     * Explicit whitespace normalization mode. When present, takes precedence
     * over {@link shouldIgnoreWhitespaceInAutograder}.
     */
    whitespaceMode?: WhitespaceMode;
  };
}

/**
 * Resolves the effective whitespace mode for a console unit tests document.
 *
 * Precedence:
 *   1. `options.whitespaceMode` (explicit) wins.
 *   2. Otherwise the legacy boolean `shouldIgnoreWhitespaceInAutograder`
 *      maps to `'strip-all'` (true) or `'exact'` (false).
 *   3. Defaults to `'exact'` when neither is set.
 *
 * Threading the resolved enum through the autograder eliminates the
 * inverted-boolean pattern (`diffOnWhitespace = !shouldIgnore...`).
 *
 * @param options Document options block, or undefined.
 * @return The effective whitespace mode.
 */
export function getWhitespaceModeForConsoleUnitTests(
  options: ConsoleUnitTestsDocument['options'],
): WhitespaceMode {
  if (options?.whitespaceMode !== undefined) {
    return options.whitespaceMode;
  }
  return getWhitespaceModeFromLegacyFlag(
    options?.shouldIgnoreWhitespaceInAutograder ?? false,
  );
}

function isValidConsoleTranscriptEntry(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Validates arbitrary Firestore data
  entry: any,
): entry is ConsoleTranscriptEntry {
  if (
    !entry ||
    typeof entry !== 'object' ||
    typeof entry.output !== 'string' ||
    !isConsoleTranscriptEntryKind(entry.type)
  ) {
    return false;
  }
  if (entry.type === 'input') {
    return typeof entry.input === 'string';
  }
  return entry.input === undefined;
}

export function isConsoleUnitTest(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  testObject: any,
): testObject is ConsoleUnitTest {
  return (
    testObject &&
    typeof testObject === 'object' &&
    typeof testObject.name === 'string' &&
    typeof testObject.description === 'string' &&
    (typeof testObject.key === 'string' ||
      typeof testObject.key === 'number') &&
    Array.isArray(testObject.transcript) &&
    testObject.transcript.every(isValidConsoleTranscriptEntry) &&
    validateOptionalPrimitiveField(testObject.options, 'object') &&
    validateOptionalPrimitiveField(
      testObject.options?.shouldIgnoreCasingInAutograder,
      'boolean',
    ) &&
    validateOptionalPrimitiveField(
      testObject.options?.shouldIgnoreWhitespaceInAutograder,
      'boolean',
    ) &&
    validateOptionalPrimitiveField(testObject.randomSeed, 'number') &&
    validateOptionalPrimitiveField(testObject.milestoneId, 'string')
  );
}

/**
 * Selects the cases that should run for one check. Cases carrying no
 * `milestoneId` belong to the assignment as a whole and are included in every
 * milestone's run.
 *
 * @param args - The cases and the selection.
 * @param args.unitTests - Every case on the assignment.
 * @param args.selectedMilestoneId - The milestone being checked. Omitted, no
 *   filtering happens and all cases are returned.
 * @return The cases to run, in their declared order.
 */
export function selectUnitTestsForMilestone({
  unitTests,
  selectedMilestoneId,
}: {
  unitTests: ConsoleUnitTest[];
  selectedMilestoneId?: string;
}): ConsoleUnitTest[] {
  if (!selectedMilestoneId) {
    return unitTests;
  }
  return unitTests.filter(
    unitTest =>
      isNullOrUndefined(unitTest.milestoneId) ||
      unitTest.milestoneId === selectedMilestoneId,
  );
}

export const CONSOLE_UNIT_TEST_DOC_CONVERTER: FirestoreDataConverter<ConsoleUnitTestsDocument> =
  {
    toFirestore(unitTests: ConsoleUnitTestsDocument): DocumentData {
      return {
        unitTests: unitTests.unitTests.map(unitTest => {
          return {
            // Wire format keeps the legacy `dialogue` field name; in-memory we
            // call it `transcript`. The shape is otherwise unchanged.
            dialogue: unitTest.transcript.map(entry => {
              if (entry.type === 'input') {
                return {
                  type: 'input' as const,
                  output: entry.output,
                  input: entry.input,
                };
              }
              return {type: 'print' as const, output: entry.output};
            }),
            name: unitTest.name,
            description: unitTest.description,
            key: unitTest.key,
            ...(typeof unitTest.randomSeed === 'number' && {
              randomSeed: unitTest.randomSeed,
            }),
            // Omitted for an unscoped case, so a round-trip through the
            // editor never stamps the field onto an assignment with no
            // milestones.
            ...(!isNullOrUndefined(unitTest.milestoneId) && {
              milestoneId: unitTest.milestoneId,
            }),
          };
        }),
        options: unitTests.options,
        lastUpdated: Timestamp.now(),
      };
    },
    fromFirestore(
      snapshot: DocumentSnapshot,
      options: SnapshotOptions,
    ): ConsoleUnitTestsDocument {
      const data = snapshot.data(options);
      if (!data) {
        console.debug('No data found in snapshot');
        return {unitTests: []};
      }
      const unitTests = data.unitTestsConsole ?? data.unitTests;
      if (!unitTests || !Array.isArray(unitTests)) {
        console.debug('No branches found in snapshot');
        return {unitTests: []};
      }
      const parsedUnitTests = unitTests
        .map(unitTest => {
          try {
            // Map wire field `dialogue` to in-memory `transcript`. Legacy data
            // may carry an empty `input: ''` on `'print'` entries; strip it so
            // the in-memory transcript always satisfies the discriminated union.
            const {dialogue, ...rest} = unitTest;
            const transcript = Array.isArray(dialogue)
              ? dialogue.map(
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Firestore data is untyped at this boundary
                  (entry: any) => {
                    if (entry?.type === 'print') {
                      return {type: 'print', output: entry.output};
                    }
                    return {
                      type: 'input',
                      output: entry?.output,
                      input: entry?.input,
                    };
                  },
                )
              : [];
            return {
              ...rest,
              transcript,
              // Legacy unit test keys are stored as numbers.
              key: String(unitTest.key),
            };
          } catch {
            console.warn('Failed to convert unit test key to string', unitTest);
            return null;
          }
        })
        .filter(unitTest => {
          if (!isConsoleUnitTest(unitTest)) {
            console.warn('Invalid unit test found in snapshot', unitTest);
            return false;
          }
          return true;
        });
      return {
        unitTests: parsedUnitTests,
        ...(data.options && {options: data.options}),
      };
    },
  };

export function getConsoleUnitTestReference(
  courseId: CourseId,
  assignmentId: AssignmentId,
) {
  return doc(
    getFirestore(),
    `/assns/${courseId}/assignments/${assignmentId}/docs/unitTests`,
  ).withConverter(CONSOLE_UNIT_TEST_DOC_CONVERTER);
}
